import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { projectsAPI, stageAssignmentsAPI } from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import "../../../components/projects/Projects.css";
import StageSubmissionView from "../../../components/projects/StageSubmissionView";
import PreviousVisitsSection from "../../../components/projects/PreviousVisitsSection";
import StageNotesSection from "../../../components/projects/StageNotesSection";
import MyInstructionsPanel from "../../../components/projects/MyInstructionsPanel";
import TimerWidget, {
  TIMER_STATES,
} from "../../../components/schedule/TimerWidget";
import { formatAssignmentStatus } from "../../../utils/formatAssignmentStatus";
import { formatDisplayDate, hasNotStartedYet } from "../../../utils/dateUtils";

export default function StageWorkPage() {
  const { id, assignmentId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sharedInfo, setSharedInfo] = useState(null);
  const [timerState, setTimerState] = useState(TIMER_STATES.NOT_STARTED);
  const [stageHistory, setStageHistory] = useState([]);

  const refreshAssignment = async () => {
    try {
      const resp = await projectsAPI.getById(id);
      const data = resp.data;
      setProject(data);
      const target = (data.stageAssignments || []).find(
        (item) => item.id === assignmentId,
      );
      setAssignment(target || null);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load stage assignment",
      );
    }
  };

  useEffect(() => {
    refreshAssignment();
  }, [id, assignmentId]);

  const handleTimerStateChange = (nextState) => {
    setTimerState((prevState) => {
      // A session just stopped (paused, auto-paused overnight, or locked) -
      // refetch so totalTimeMinutes reflects the backend, not the
      // page-load-time snapshot the timer's base is computed from.
      if (
        prevState === TIMER_STATES.RUNNING &&
        nextState !== TIMER_STATES.RUNNING
      ) {
        refreshAssignment();
      }
      return nextState;
    });
  };

  useEffect(() => {
    if (!assignment || !project) return;
    const loadHistory = async () => {
      try {
        const resp = await stageAssignmentsAPI.getStageHistory(
          id,
          assignment.stageId,
        );
        const list = resp.data || [];
        const currentVisit = assignment.visitNumber || 1;
        setStageHistory(list.filter((item) => item.visitNumber < currentVisit));
      } catch (err) {
        setStageHistory([]);
      }
    };
    loadHistory();
  }, [assignment, project, id]);

  const stage = useMemo(() => {
    if (!project || !assignment) return null;
    return (project.workflow?.definition?.stages || []).find(
      (item) => item.id === assignment.stageId,
    );
  }, [project, assignment]);

  const previousAssignments = useMemo(() => {
    if (!project || !assignment) return [];
    return (project.stageAssignments || [])
      .filter(
        (item) =>
          item.stageId === assignment.stageId && item.id !== assignment.id,
      )
      .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
  }, [project, assignment]);

  const stageAssignmentsForStage = useMemo(() => {
    if (!project || !assignment) return [];
    return (project.stageAssignments || [])
      .filter((item) => item.stageId === assignment.stageId)
      .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
  }, [project, assignment]);

  const currentVisitNumber = useMemo(() => {
    if (!stageAssignmentsForStage.length) return assignment?.visitNumber || 1;
    return Math.max(
      ...stageAssignmentsForStage.map((item) => item.visitNumber || 1),
    );
  }, [stageAssignmentsForStage, assignment?.visitNumber]);
  const currentVisitAssignments = useMemo(() => {
    return stageAssignmentsForStage.filter(
      (item) => (item.visitNumber || 1) === currentVisitNumber,
    );
  }, [stageAssignmentsForStage, currentVisitNumber]);

  const sharedAssignment = useMemo(() => {
    return (
      currentVisitAssignments.find(
        (item) => (item.taskSubmissions || []).length > 0,
      ) || null
    );
  }, [currentVisitAssignments]);

  const isSharedOwner =
    !sharedAssignment || sharedAssignment.id === assignment?.id;

  const ownSubmissions = assignment?.taskSubmissions || [];
  const displaySubmissions = useMemo(() => {
    const others = currentVisitAssignments
      .filter((item) => item.id !== assignment?.id)
      .flatMap((item) => item.taskSubmissions || [])
      .sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
    return [...ownSubmissions, ...others];
  }, [currentVisitAssignments, assignment?.id, ownSubmissions]);

  const buildSubmissionMap = (items) => {
    const map = {};
    items.forEach((item) => {
      if (map[item.taskId]) return;
      map[item.taskId] = {
        taskId: item.taskId,
        taskTitle: item.taskTitle,
        taskType: item.taskType,
        textValue: item.textValue || "",
        fileName: item.fileName || "",
        fileBase64: item.filePath || "",
        fileSize: item.fileSize || 0,
      };
    });
    return map;
  };

  useEffect(() => {
    if (!assignment) return;
    if (isSharedOwner) {
      setSubmissions(buildSubmissionMap(ownSubmissions));
      setSharedInfo(null);
    } else if (sharedAssignment) {
      setSubmissions({});
      setSharedInfo({
        assignee: sharedAssignment.assignee,
        assignedAt: sharedAssignment.assignedAt,
      });
    }
  }, [assignment, isSharedOwner, displaySubmissions, sharedAssignment]);

  const displayMap = useMemo(
    () => buildSubmissionMap(displaySubmissions),
    [displaySubmissions],
  );
  const submittedTaskIds = useMemo(() => {
    return new Set((displaySubmissions || []).map((item) => item.taskId));
  }, [displaySubmissions]);
  const remainingTasks = useMemo(() => {
    return (stage?.tasks || []).filter(
      (task) => !submittedTaskIds.has(task.id),
    );
  }, [stage?.tasks, submittedTaskIds]);
  const submittedTasks = useMemo(() => {
    return (stage?.tasks || []).filter((task) => submittedTaskIds.has(task.id));
  }, [stage?.tasks, submittedTaskIds]);
  const readOnlyStatus = assignment
    ? [
        "SUBMITTED",
        "DONE",
        "RETURNED",
        "REJECTED",
        "REASSIGNED",
        "REMOVED",
      ].includes(assignment.status)
    : false;
  const canSubmit =
    timerState === TIMER_STATES.RUNNING || timerState === TIMER_STATES.PAUSED;
  const canSubmitWithoutNewTasks = remainingTasks.length === 0;
  // Submitting needs a timer session, unless every task is already answered by
  // someone on this visit.
  const submitBlocked = !canSubmit && !canSubmitWithoutNewTasks;
  const startDate = assignment?.startDate
    ? new Date(assignment.startDate)
    : null;
  const hasNotStartedYetValue = hasNotStartedYet(assignment?.startDate);

  const hasExistingValue = (taskId) => {
    const entry = displayMap[taskId];
    if (!entry) return false;
    if (entry.taskType === "text") return !!entry.textValue;
    if (entry.taskType === "file") {
      return !!entry.fileName || !!entry.fileBase64;
    }
    if (entry.taskType === "checkbox") return entry.textValue === "true";
    return false;
  };

  if (!project || !assignment || !stage) {
    return <div className="stage-work-page">{error || "Loading..."}</div>;
  }

  if (assignment.assigneeId !== user?.id) {
    return (
      <div className="stage-work-page">You are not assigned to this stage.</div>
    );
  }

  // Booked ahead but the workflow hasn't reached this stage. The server refuses
  // work here too; this just explains why instead of showing a failing timer.
  const projectStageIds =
    project.currentStageIds?.length
      ? project.currentStageIds
      : project.currentStageId
        ? [project.currentStageId]
        : [];
  const stageNotActiveYet =
    ["PENDING", "IN_PROGRESS"].includes(assignment.status) &&
    !projectStageIds.includes(assignment.stageId);

  if (stageNotActiveYet) {
    return (
      <div className="stage-work-page">
        <div className="panel" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
          <h2 style={{ marginTop: 0 }}>This stage isn't active yet</h2>
          <p className="small-muted">
            You're assigned to this stage, but the project hasn't reached it.
            You'll be able to start work once the earlier stages are done.
          </p>
        </div>
      </div>
    );
  }

  if (hasNotStartedYetValue) {
    return (
      <div className="stage-work-page">
        <div className="panel" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🗓️</div>
          <h2 style={{ marginTop: 0 }}>This work hasn't started yet</h2>
          <p>
            You're scheduled to begin{" "}
            <strong>
              {formatDisplayDate(startDate, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </strong>
          </p>
          <p className="small-muted">
            You'll be able to open this stage and start logging time from that
            date. Check back then.
          </p>
        </div>
      </div>
    );
  }

  const onTextChange = (task, value) => {
    setSubmissions((prev) => ({
      ...prev,
      [task.id]: {
        taskId: task.id,
        taskTitle: task.title || task.name || "Task",
        taskType: "text",
        textValue: value,
      },
    }));
  };

  const onCheckboxChange = (task, checked) => {
    setSubmissions((prev) => ({
      ...prev,
      [task.id]: {
        taskId: task.id,
        taskTitle: task.title || task.name || "Task",
        taskType: "checkbox",
        textValue: checked ? "true" : "",
      },
    }));
  };

  const onFileChange = (task, file) => {
    if (!file) return;

    setSubmissions((prev) => ({
      ...prev,
      [task.id]: {
        taskId: task.id,
        taskTitle: task.title || task.name || "Task",
        taskType: "file",
        fileKey: `file_${task.id}`,
        fileObject: file,
      },
    }));
  };

  const submit = async (action) => {
    setSaving(true);
    setError("");

    try {
      const formData = new FormData();

      const submissionArray = Object.values(submissions)
        .filter((item) => !submittedTaskIds.has(item.taskId))
        .map((item) => ({
          taskId: item.taskId,
          taskTitle: item.taskTitle,
          taskType: item.taskType,
          textValue: item.textValue || null,
          fileKey: item.fileKey || null,
        }));

      formData.append("submissions", JSON.stringify(submissionArray));
      formData.append("action", action);

      Object.values(submissions).forEach((item) => {
        if (item.taskType === "file" && item.fileObject && item.fileKey) {
          formData.append(item.fileKey, item.fileObject);
        }
      });

      await stageAssignmentsAPI.submitTasks(id, assignmentId, formData);

      const refreshed = await projectsAPI.getById(id);
      const nextAssignment = (refreshed.data.stageAssignments || []).find(
        (item) => item.id === assignmentId,
      );

      setProject(refreshed.data);
      setAssignment(nextAssignment);
    } catch (err) {
      if (err.response?.status === 409) {
        try {
          const refreshed = await projectsAPI.getById(id);
          const nextAssignment = (refreshed.data.stageAssignments || []).find(
            (item) => item.id === assignmentId,
          );
          setProject(refreshed.data);
          setAssignment(nextAssignment);
        } catch {
          // ignore refresh failure
        }
        setError(
          err.response?.data?.message ||
            "Another assignee already submitted this task.",
        );
      } else {
        setError(err.response?.data?.message || "Failed to submit stage tasks");
      }
    } finally {
      setSaving(false);
    }
  };

  const statusInfo = formatAssignmentStatus(assignment.status);
  const isRevisit = (assignment.visitNumber || 1) > 1;

  return (
    <div className="stage-work-page">
      <Link className="back-link" to={`/projects/${id}`}>
        ← Back to Project
      </Link>

      <div className="stage-work-header">
        <div className="stage-work-heading">
          <h1 className="stage-work-title">{assignment.stageName}</h1>
          <div className="stage-work-subtitle">
            {project.name}
            {isRevisit && (
              <span className="stage-work-visit">
                ↩ Visit {assignment.visitNumber}
              </span>
            )}
          </div>
        </div>
        <span
          className="stage-work-status"
          style={{
            backgroundColor: statusInfo.bgColor,
            color: statusInfo.textColor,
          }}
        >
          {statusInfo.label}
        </span>
      </div>

      {readOnlyStatus && stage?.sendForReview !== false && (
        <div className="stage-work-banner">Submitted — awaiting review.</div>
      )}
      {!isSharedOwner && sharedInfo && (
        <div className="stage-work-banner">
          Some fields were already submitted by {sharedInfo.assignee?.firstName}{" "}
          {sharedInfo.assignee?.lastName}. You can complete the remaining
          fields.
        </div>
      )}

      <div className="stage-work-layout">
        <div className="stage-work-main">
          {stageHistory.length > 0 && (
            <PreviousVisitsSection
              history={stageHistory}
              currentVisitNumber={assignment.visitNumber || 1}
            />
          )}

          <MyInstructionsPanel
            projectId={id}
            stageId={assignment.stageId}
            assignmentId={assignment.id}
          />

          <div className="panel" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Tasks</h3>
            {submittedTasks.length > 0 && (
              <div className="small-muted" style={{ marginBottom: 10 }}>
                Submitted tasks:{" "}
                {submittedTasks
                  .map((task) => task.title || task.name || "Task")
                  .join(", ")}
              </div>
            )}
            <div className="tasks-list">
              {remainingTasks.map((task) => (
                <div key={task.id}>
                  <label>
                    {task.title || task.name || "Task"}{" "}
                    {task.mandatory ? "*" : ""}
                  </label>
                  {(task.type || "text") === "file" ? (
                    <>
                      {(() => {
                        const canEdit =
                          !readOnlyStatus &&
                          (!hasExistingValue(task.id) || isSharedOwner);
                        return (
                          <>
                            {canEdit && (
                              <input
                                type="file"
                                onChange={(e) =>
                                  onFileChange(task, e.target.files?.[0])
                                }
                              />
                            )}
                            {displayMap[task.id]?.fileName && (
                              <div
                                className="small-muted"
                                style={{ marginTop: 4 }}
                              >
                                File: {displayMap[task.id].fileName}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (task.type || "text") === "checkbox" ? (
                    (() => {
                      const canEdit =
                        !readOnlyStatus &&
                        (!hasExistingValue(task.id) || isSharedOwner);
                      const displayChecked =
                        displayMap[task.id]?.textValue === "true";
                      const hasDraft =
                        submissions[task.id] !== undefined &&
                        submissions[task.id] !== null;
                      const draftChecked =
                        submissions[task.id]?.textValue === "true";
                      const checkedValue =
                        readOnlyStatus || hasExistingValue(task.id)
                          ? displayChecked
                          : hasDraft
                            ? draftChecked
                            : displayChecked;
                      return (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 6,
                          }}
                        >
                          <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked={checkedValue}
                            onChange={(e) =>
                              onCheckboxChange(task, e.target.checked)
                            }
                            style={{ accentColor: "#4f46e5" }}
                          />
                          <span>
                            {task.checkboxLabel || "I confirm this is done"}
                          </span>
                        </label>
                      );
                    })()
                  ) : (
                    <textarea
                      rows={3}
                      disabled={
                        readOnlyStatus ||
                        (hasExistingValue(task.id) && !isSharedOwner)
                      }
                      value={
                        readOnlyStatus || hasExistingValue(task.id)
                          ? displayMap[task.id]?.textValue || ""
                          : submissions[task.id]?.textValue ||
                            displayMap[task.id]?.textValue ||
                            ""
                      }
                      onChange={(e) => onTextChange(task, e.target.value)}
                    />
                  )}
                </div>
              ))}
              {remainingTasks.length === 0 && (
                <div className="small-muted">All tasks have submissions.</div>
              )}
            </div>
            {!readOnlyStatus && (
              <div className="stage-work-submit-bar">
                <button
                  className="btn-primary"
                  disabled={saving || submitBlocked}
                  onClick={() => submit("SUBMIT")}
                >
                  {stage?.sendForReview === false
                    ? "Submit"
                    : "Submit for Review"}
                </button>
                {submitBlocked && (
                  <span className="stage-work-submit-hint">
                    Start the timer to submit this stage.
                  </span>
                )}
              </div>
            )}
            {error && (
              <div className="small-muted" style={{ color: "#b91c1c" }}>
                {error}
              </div>
            )}
          </div>
        </div>

        <aside className="stage-work-aside">
          <TimerWidget
            stageAssignmentId={assignment.id}
            totalTimeMinutes={assignment.totalTimeMinutes || 0}
            onStateChange={handleTimerStateChange}
            isLocked={readOnlyStatus}
          />

          <div className="panel stage-work-meta">
            <h4>Stage details</h4>
            <dl>
              <div>
                <dt>Assigned by</dt>
                <dd>
                  {assignment.assignedBy?.firstName}{" "}
                  {assignment.assignedBy?.lastName}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{statusInfo.label}</dd>
              </div>
              {assignment.startDate && (
                <div>
                  <dt>Start</dt>
                  <dd>
                    {formatDisplayDate(new Date(assignment.startDate), {
                      day: "numeric",
                      month: "short",
                    })}
                  </dd>
                </div>
              )}
              {assignment.expectedEndDate && (
                <div>
                  <dt>Due</dt>
                  <dd>
                    {formatDisplayDate(new Date(assignment.expectedEndDate), {
                      day: "numeric",
                      month: "short",
                    })}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <StageNotesSection
            projectId={id}
            stageId={assignment.stageId}
            assignmentId={assignment.id}
            canAdd={!readOnlyStatus}
          />

          {previousAssignments.length > 0 && (
            <details className="panel stage-work-history">
              <summary>Stage history ({previousAssignments.length})</summary>
              {previousAssignments.map((item) => (
                <div key={item.id} className="stage-work-history-item">
                  <div className="small-muted">
                    {item.assignee?.firstName} {item.assignee?.lastName} ·{" "}
                    {formatAssignmentStatus(item.status).label}
                  </div>
                  <StageSubmissionView
                    submissions={item.taskSubmissions || []}
                  />
                </div>
              ))}
            </details>
          )}
        </aside>
      </div>
    </div>
  );
}
